// js/journal-calendar.js
// Calendario Integrado para Trading Journal con datos de Supabase

class JournalCalendar {
    constructor() {
        this.currentDate = new Date();
        this.selectedDay = null;
        this.tradesData = {}; // Formato: {'YYYY-MM-DD': [trades]}
        this.currentAccountId = null;
        
        // ✅ FASE 1: NUEVAS PROPIEDADES PARA ARQUITECTURA PROFESIONAL
        
        // Cache precalculado de métricas anuales
        // Estructura: { year: { token: timestamp, months: { 0: {perf, ops, wr, avg, wins, losses}, ... } } }
        this.annualCache = {};
        
        // Índice de acceso rápido para fechas por mes
        // Estructura: { year: { month: ['2025-03-01', '2025-03-02', ...] } }
        this.monthlyIndexes = {};
        
        // Estado de sincronización
        this.syncState = {
            viewMode: 'monthly',  // 'monthly' | 'annual'
            currentDate: new Date(),
            sync_paused_reason: null,  // null | 'viewing_annual'
            deltasQueue: [],  // Array de cambios acumulados desde MetaTrader
            annualCacheToken: null,  // Token para invalidar caché
            monthlyIndexesToken: null  // Token para invalidar índices
        };
        
        // Control de estado anterior (para restaurar estilos)
        this._previousStyles = {};
        
        this.init();
    }

    init() {
        this.setupEventListeners();
        // El calendario se renderizará cuando se carguen los trades de una cuenta
    }

    setupEventListeners() {
        const prevBtn = document.getElementById('prevMonth');
        const nextBtn = document.getElementById('nextMonth');
        const monthlyViewBtn = document.getElementById('monthlyViewBtn');
        const annualViewBtn = document.getElementById('annualViewBtn');
        
        if (prevBtn) prevBtn.addEventListener('click', () => {
            if (this.syncState.viewMode === 'annual') this.changeYear(-1);
            else this.changeMonth(-1);
        });
        if (nextBtn) nextBtn.addEventListener('click', () => {
            if (this.syncState.viewMode === 'annual') this.changeYear(1);
            else this.changeMonth(1);
        });
        if (monthlyViewBtn) monthlyViewBtn.addEventListener('click', () => this.switchViewMode('monthly'));
        if (annualViewBtn) annualViewBtn.addEventListener('click', () => this.switchViewMode('annual'));
    }

    // ✅ FASE 2.1: PRECALCULAR ÍNDICE DE MESES
    /**
     * Crea índice O(1) para acceso a fechas por mes
     * Llamar después de syncFromWindowData()
     */
    calculateMonthlyIndexes(journalData) {
        this.monthlyIndexes = {};
        
        Object.keys(journalData || {}).forEach(dateStr => {
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = date.getMonth();
            
            if (!this.monthlyIndexes[year]) {
                this.monthlyIndexes[year] = {};
            }
            if (!this.monthlyIndexes[year][month]) {
                this.monthlyIndexes[year][month] = [];
            }
            
            this.monthlyIndexes[year][month].push(dateStr);
        });
        
        // Ordenar cada mes
        Object.keys(this.monthlyIndexes).forEach(year => {
            Object.keys(this.monthlyIndexes[year]).forEach(month => {
                this.monthlyIndexes[year][month].sort();
            });
        });
        
        this.syncState.monthlyIndexesToken = Date.now();
    }

    // ✅ FASE 2.2: PRECALCULAR MÉTRICAS ANUALES (UNA SOLA PASADA)
    /**
     * Calcula todas las métricas de 12 meses en una únicapasada sobre los datos
     * Llamar en switchViewMode('annual')
     */
    calculateAnnualMetrics(year, dataSource = null) {
        const source = dataSource || window.journalData || {};
        
        // Inicializar estructura para el año
        this.annualCache[year] = {
            token: this.syncState.annualCacheToken,
            months: {}
        };
        
        // Inicializar 12 meses con estructura vacía
        for (let month = 0; month < 12; month++) {
            this.annualCache[year].months[month] = {
                perf: 0,        // Total profit USD
                ops: 0,         // Total trades
                wr: 0,          // Win rate % (excluye BE)
                avg: 0,         // Avg profit per trade
                wins: 0,        // Count of winning trades
                losses: 0,      // Count of losing trades
                beCount: 0,     // Break-even trades (±0.1% balance)
                grossProfit: 0, // Sum of positive trades
                grossLoss: 0    // Sum of negative trades
            };
        }
        
        // Obtener balance inicial para cálculo de BE (igual que en vista mensual)
        const accountData = window.accounts?.[window.currentAccount];
        const initialBalance = accountData?.initial_balance || accountData?.balance || 10000;

        // ✅ UNA SOLA PASADA sobre los datos del año
        Object.keys(source).forEach(dateStr => {
            const date = new Date(dateStr);
            
            // Solo procesar el año solicitado
            if (date.getFullYear() !== year) {
                return;
            }
            
            const month = date.getMonth();
            const trades = source[dateStr] || [];
            
            // Procesar trades del día
            trades.forEach(trade => {
                const profit = parseFloat(trade.profitUSD || 0);
                const m = this.annualCache[year].months[month];
                const profitPct = (profit / initialBalance) * 100;
                const isBE = profitPct >= -0.1 && profitPct <= 0.1;
                
                m.perf += profit;
                m.ops += 1;
                
                if (isBE) {
                    m.beCount++;
                } else if (profit > 0) {
                    m.wins++;
                    m.grossProfit += profit;
                } else {
                    m.losses++;
                    m.grossLoss += Math.abs(profit);
                }
            });
        });
        
        // Calcular métricas derivadas (después de acumular)
        for (let month = 0; month < 12; month++) {
            const m = this.annualCache[year].months[month];
            
            if (m.ops > 0) {
                // Win rate excluyendo BE (igual que vista mensual)
                const nonBE = m.ops - m.beCount;
                m.wr = nonBE > 0 ? Math.round((m.wins / nonBE) * 100) : 0;
                m.avg = (m.perf / m.ops).toFixed(2);
            }
        }
        
        // Guardar token para comparación futura
        this.annualCache[year].token = this.syncState.annualCacheToken;
    }

    // ✅ FASE 2.3: GUARDAR/RESTAURAR ESTILOS
    saveStyleState() {
        // Ya no es necesario - los cambios se manejan directamente en switchViewMode()
        this._previousStyles = {};
    }

    restoreStyleState() {
        // Ya no es necesario - los cambios se manejan directamente en switchViewMode()
    }

    changeMonth(direction) {
        this.currentDate.setMonth(this.currentDate.getMonth() + direction);
        // No llamar renderCalendar() aquí - lo hace window.changeMonth() en app.js
        // evitando el flash del layout por filas
        this.updateMonthStats();
    }

    async changeYear(direction) {
        this.currentDate.setFullYear(this.currentDate.getFullYear() + direction);
        // Invalidar caché del nuevo año
        this.syncState.annualCacheToken = Date.now();
        await this.switchViewMode('annual');
    }

    // Método para cargar trades desde los datos ya existentes del journal
    loadTradesFromData(trades, accountId) {
        this.currentAccountId = accountId;
        this.tradesData = {};

        // Convertir array de trades a formato por día
        trades.forEach(trade => {
            const dateStr = trade.trade_date; // Ya viene en formato YYYY-MM-DD desde Supabase
            if (!this.tradesData[dateStr]) {
                this.tradesData[dateStr] = [];
            }
            this.tradesData[dateStr].push({
                id: trade.id,
                pair: trade.pair,
                type: trade.direction,
                profit: parseFloat(trade.profit_usd),
                time: trade.entry_time || '00:00',
                lotSize: trade.lot_size,
                comment: trade.comment,
                tradingViewUrl: trade.tradingview_url
            });
        });

        this.renderCalendar();
        this.updateMonthStats();
    }

    // ✅ FASE 4.1: SINCRONIZADOR MEJORADO desde window.journalData (formato app.js)
    /**
     * Sincroniza datos desde window.journalData (formato de app.js)
     * Llamado desde: app.js doFullRefresh() cuando journalData cambió
     * Responsabilidades: convertir + actualizar índices + invalidar cachés
     */
    syncFromWindowData() {
        if (!window.journalData) return;
        
        // 1. Convertir window.journalData a tradesData local (formato esperado)
        this.tradesData = {};
        Object.keys(window.journalData).forEach(dateStr => {
            const dayTrades = window.journalData[dateStr] || [];
            this.tradesData[dateStr] = dayTrades.map(trade => ({
                id: trade.id,
                pair: trade.pair || trade.symbol,
                type: trade.direction || trade.type,
                profit: parseFloat(trade.profitUSD || trade.profit_usd || 0),
                time: trade.entryTime || trade.entry_time || '00:00',
                lotSize: trade.lotSize || trade.lot_size,
                comment: trade.comment,
                tradingViewUrl: trade.tradingViewUrl || trade.tradingview_url
            }));
        });
        
        // 2. Recalcular índices de acceso rápido
        this.calculateMonthlyIndexes(window.journalData);
        
        // 3. Invalidar caché de métricas anuales
        this.syncState.annualCacheToken = Date.now();
        
        // 4. NO renderizar aquí - lo hace switchViewMode() o updateMonthStats()
    }

    // ✅ FASE 4.2: CAMBIO DE VISTA MEJORADO
    /**
     * Cambiar entre vista mensual y anual
     * Responsabilidades:
     *   - Pausar/reanudar auto-sync
     *   - Precalcular cachés
     *   - Guardar/restaurar estilos
     *   - Renderizar apropiadamente
     */
    async switchViewMode(mode) {
        // Guardar estado ANTES de cambiar a annual
        if (mode === 'annual' && this.syncState.viewMode === 'monthly') {
            this.saveStyleState();
        }
        
        this.syncState.viewMode = mode;
        
        // Actualizar UI de botones
        const monthlyViewBtn = document.getElementById('monthlyViewBtn');
        const annualViewBtn = document.getElementById('annualViewBtn');
        
        if (monthlyViewBtn && annualViewBtn) {
            if (mode === 'monthly') {
                monthlyViewBtn.classList.add('bg-slate-700', 'text-white');
                monthlyViewBtn.classList.remove('text-slate-400', 'hover:text-white');
                annualViewBtn.classList.remove('bg-slate-700', 'text-white');
                annualViewBtn.classList.add('text-slate-400', 'hover:text-white');
            } else {
                annualViewBtn.classList.add('bg-slate-700', 'text-white');
                annualViewBtn.classList.remove('text-slate-400', 'hover:text-white');
                monthlyViewBtn.classList.remove('bg-slate-700', 'text-white');
                monthlyViewBtn.classList.add('text-slate-400', 'hover:text-white');
            }
        }
        
        // LÓGICA ESPECÍFICA POR MODO
        if (mode === 'annual') {
            // ✅ PAUSAR AUTO-SYNC
            this.syncState.sync_paused_reason = 'viewing_annual';
            this.syncState.deltasQueue = [];
            
            const year = this.currentDate.getFullYear();
            
            // ✅ CARGAR TODOS LOS TRADES DEL AÑO (no solo el mes actual)
            if (typeof window.loadYearTradesForAnnualView === 'function') {
                this._annualYearData = await window.loadYearTradesForAnnualView(year);
            } else {
                this._annualYearData = window.journalData || {};
            }
            
            // ✅ PRECALCULAR CACHÉ (siempre recalcular al entrar a anual)
            this.calculateAnnualMetrics(year, this._annualYearData);
            
            // ✅ OCULTAR ELEMENTOS MENSUAL
            const daysHeader = document.getElementById('calWeekdaysHeader');
            if (daysHeader) {
                daysHeader.style.setProperty('display', 'none', 'important');
            }
            
            const mobileRecapContainer = document.getElementById('mobileWeeklyRecap')?.parentElement;
            if (mobileRecapContainer) {
                mobileRecapContainer.classList.add('hidden');
            }

            // ✅ OCULTAR botón de eventos en vista anual
            const syncEventsBtn = document.getElementById('syncEventsBtn');
            if (syncEventsBtn) syncEventsBtn.style.setProperty('display', 'none', 'important');

            // ✅ RESETEAR clases de calendarDays para layout anual
            const calDays = document.getElementById('calendarDays');
            if (calDays) {
                this._calendarDaysOriginalClass = calDays.className;
                calDays.className = '';
            }
            
            // ✅ RENDERIZAR VISTA ANUAL
            this.renderAnnualCalendar();
            this.updateAnnualMetrics();
            
        } else if (mode === 'monthly') {
            // ✅ REANUDAR AUTO-SYNC
            this.syncState.sync_paused_reason = null;

            // ✅ RESTAURAR labels de periodo a "Mes"
            const tradesPeriodLabel = document.getElementById('tradesPeriodLabel');
            if (tradesPeriodLabel) tradesPeriodLabel.textContent = 'Trades Mes';
            const highlightsPeriodLabel = document.getElementById('highlightsPeriodLabel');
            if (highlightsPeriodLabel) highlightsPeriodLabel.textContent = 'Destacados del Mes';
            const durationPeriodLabel = document.getElementById('durationPeriodLabel');
            if (durationPeriodLabel) durationPeriodLabel.textContent = '(semanal)';
            
            // ✅ MOSTRAR ELEMENTOS MENSUAL
            const daysHeader = document.getElementById('calWeekdaysHeader');
            if (daysHeader) {
                daysHeader.style.removeProperty('display');
            }
            
            const mobileRecapContainer = document.getElementById('mobileWeeklyRecap')?.parentElement;
            if (mobileRecapContainer) {
                mobileRecapContainer.classList.remove('hidden');
            }

            // ✅ RESTAURAR visibilidad del botón de eventos (lo controla app.js)
            const syncEventsBtn = document.getElementById('syncEventsBtn');
            if (syncEventsBtn) syncEventsBtn.style.removeProperty('display');

            // ✅ RESTAURAR clases originales de calendarDays
            const calDays = document.getElementById('calendarDays');
            if (calDays && this._calendarDaysOriginalClass) {
                calDays.className = this._calendarDaysOriginalClass;
            }
            
            // ✅ RESTAURAR ESTILOS GUARDADOS
            this.restoreStyleState();
            
            // ✅ RENDERIZAR VISTA MENSUAL completa (igual que al cambiar de mes)
            if (typeof window.updateDisplay === 'function') {
                window.updateDisplay();
            } else {
                this.renderCalendar();
            }
            // ✅ RESTAURAR KPI CARDS con métricas mensuales correctas
            if (typeof window.updateRealTimeMetrics === 'function') {
                window.updateRealTimeMetrics(this.currentDate);
            }
        }
    }

    renderCalendar() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Actualizar header
        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                           'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const currentMonthEl = document.getElementById('currentMonth');
        if (currentMonthEl) {
            currentMonthEl.textContent = `${monthNames[month]} ${year}`;
        }

        // Calcular días del mes
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const calendarDays = document.getElementById('calendarDays');
        if (!calendarDays) return;
        
        calendarDays.innerHTML = '';

        let currentDayIndex = 0;

        // Calcular número de semanas
        const totalDays = firstDay + daysInMonth;
        const numWeeks = Math.ceil(totalDays / 7);

        for (let week = 0; week < numWeeks; week++) {
            // Renderizar 7 días de la semana
            for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
                const currentDay = (week * 7) + dayOfWeek - firstDay + 1;
                
                if (currentDay < 1 || currentDay > daysInMonth) {
                    // Días vacíos antes o después del mes
                    const emptyDay = document.createElement('div');
                    emptyDay.className = 'calendar-day calendar-day-empty opacity-50';
                    calendarDays.appendChild(emptyDay);
                } else {
                    // Días del mes
                    const day = currentDay;
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayTrades = this.tradesData[dateStr] || [];
                    const dayProfit = dayTrades.reduce((sum, trade) => sum + trade.profit, 0);
                    
                    const dayElement = document.createElement('div');
                    dayElement.className = 'calendar-day min-h-[70px] lg:min-h-[100px]';
                    
                    // Determinar clase según profit
                    if (dayTrades.length > 0) {
                        if (dayProfit >= 0) {
                            dayElement.classList.add('calendar-day-profit');
                        } else {
                            dayElement.classList.add('calendar-day-loss');
                        }
                    } else {
                        dayElement.classList.add('calendar-day-empty');
                    }

                    // Número del día
                    const dayNumber = document.createElement('div');
                    dayNumber.className = dayTrades.length > 0 ? 'text-base lg:text-lg font-bold' : 'text-sm lg:text-base font-medium';
                    dayNumber.textContent = day;
                    dayElement.appendChild(dayNumber);

                    // Solo mostrar info si hay trades
                    if (dayTrades.length > 0) {
                        // Indicador de actividad
                        const indicator = document.createElement('div');
                        indicator.className = 'absolute top-1 right-1 w-2 h-2 bg-blue-400 rounded-full';
                        dayElement.appendChild(indicator);
                        
                        // Info compacta en el bottom
                        const info = document.createElement('div');
                        info.className = 'mt-auto space-y-0.5';
                        
                        const tradesCount = document.createElement('div');
                        const isProfit = dayProfit >= 0;
                        tradesCount.className = `text-[10px] lg:text-xs font-medium ${isProfit ? 'text-green-300/80' : 'text-red-300/80'}`;
                        tradesCount.textContent = `${dayTrades.length} trade${dayTrades.length > 1 ? 's' : ''}`;
                        info.appendChild(tradesCount);
                        
                        const profitAmount = document.createElement('div');
                        profitAmount.className = `text-xs lg:text-sm font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`;
                        profitAmount.textContent = `${isProfit ? '+' : ''}$${Math.abs(dayProfit).toFixed(0)}`;
                        info.appendChild(profitAmount);
                        
                        dayElement.appendChild(info);

                        // Click para abrir modal existente del journal
                        dayElement.addEventListener('click', () => {
                            if (window.openDayForDate) {
                                window.openDayForDate(dateStr);
                            }
                        });
                    } else {
                        // Día sin trades - abrir modal para ese día
                        dayElement.addEventListener('click', () => {
                            if (window.openDayForDate) {
                                window.openDayForDate(dateStr);
                            }
                        });
                    }

                    calendarDays.appendChild(dayElement);
                }
            }

            // Recap semanal (solo desktop, columna 8)
            if (window.innerWidth >= 1024) {
                const weekRecap = this.createWeekRecap(week + 1, year, month, firstDay, week, daysInMonth);
                calendarDays.appendChild(weekRecap);
            }
        }

        // Render mobile recap
        this.renderMobileRecap(year, month, daysInMonth, firstDay);
    }

    createWeekRecap(weekNumber, year, month, firstDay, weekIndex, daysInMonth) {
        const recap = document.createElement('div');
        recap.className = 'hidden lg:flex lg:flex-col calendar-day bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-blue-500/30 p-3 gap-2';

        // Calcular rango de días de esta semana
        const weekStartDay = Math.max(1, (weekIndex * 7) - firstDay + 1);
        const weekEndDay = Math.min(weekStartDay + 6, daysInMonth);

        // Calcular stats de la semana
        let weekWins = 0;
        let weekLosses = 0;
        let weekProfit = 0;

        for (let day = weekStartDay; day <= weekEndDay; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayTrades = this.tradesData[dateStr] || [];
            dayTrades.forEach(trade => {
                if (trade.profit >= 0) weekWins++;
                else weekLosses++;
                weekProfit += trade.profit;
            });
        }

        const weekTotal = weekWins + weekLosses;
        const weekWR = weekTotal > 0 ? Math.round((weekWins / weekTotal) * 100) : 0;
        const isProfit = weekProfit >= 0;

        recap.innerHTML = `
            <div class="text-[10px] text-slate-500 uppercase tracking-wide">Semana ${weekNumber}</div>
            <div class="text-lg font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}">
                ${isProfit ? '+' : ''}$${Math.abs(weekProfit).toLocaleString()}
            </div>
            <div class="grid grid-cols-2 gap-2 pt-2 border-t border-slate-700/30">
                <div>
                    <div class="flex items-center gap-1">
                        <span class="w-1 h-1 rounded-full bg-green-400"></span>
                        <span class="text-[10px] text-slate-400">Wins</span>
                    </div>
                    <div class="text-xs font-semibold text-green-400">${weekWins}</div>
                </div>
                <div>
                    <div class="flex items-center gap-1">
                        <span class="w-1 h-1 rounded-full bg-red-400"></span>
                        <span class="text-[10px] text-slate-400">Loss</span>
                    </div>
                    <div class="text-xs font-semibold text-red-400">${weekLosses}</div>
                </div>
            </div>
            <div class="pt-2 border-t border-slate-700/30 mt-auto">
                <div class="flex items-center justify-between">
                    <span class="text-[10px] text-slate-400">Win Rate</span>
                    <span class="text-sm font-bold text-blue-400">${weekWR}%</span>
                </div>
            </div>
        `;

        return recap;
    }

    renderMobileRecap(year, month, daysInMonth, firstDay) {
        const mobileRecap = document.getElementById('mobileWeeklyRecap');
        if (!mobileRecap) return;

        // Verificar si hay trades en el mes
        let hasTradesInMonth = false;
        Object.keys(this.tradesData).forEach(dateStr => {
            const date = new Date(dateStr);
            if (date.getFullYear() === year && date.getMonth() === month) {
                hasTradesInMonth = true;
            }
        });

        // Si no hay trades, ocultar la sección completa
        const mobileRecapContainer = mobileRecap.parentElement;
        if (!hasTradesInMonth) {
            if (mobileRecapContainer) {
                mobileRecapContainer.style.display = 'none';
            }
            return;
        } else {
            if (mobileRecapContainer) {
                mobileRecapContainer.style.display = '';
            }
        }

        let html = '';
        const totalDays = firstDay + daysInMonth;
        const numWeeks = Math.ceil(totalDays / 7);

        for (let week = 0; week < numWeeks; week++) {
            const weekStartDay = Math.max(1, (week * 7) - firstDay + 1);
            const weekEndDay = Math.min(weekStartDay + 6, daysInMonth);

            // Calcular stats de la semana
            let weekWins = 0;
            let weekLosses = 0;
            let weekProfit = 0;

            for (let day = weekStartDay; day <= weekEndDay; day++) {
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayTrades = this.tradesData[dateStr] || [];
                dayTrades.forEach(trade => {
                    if (trade.profit >= 0) weekWins++;
                    else weekLosses++;
                    weekProfit += trade.profit;
                });
            }

            const weekTotal = weekWins + weekLosses;
            const weekWR = weekTotal > 0 ? Math.round((weekWins / weekTotal) * 100) : 0;
            const isProfit = weekProfit >= 0;

            html += `
                <div class="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 hover:border-slate-600 transition-colors">
                    <div class="flex-shrink-0">
                        <div class="text-xs text-slate-500 uppercase mb-1">Semana ${week + 1}</div>
                        <div class="text-xl font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}">
                            ${isProfit ? '+' : ''}$${Math.abs(weekProfit).toLocaleString()}
                        </div>
                    </div>
                    <div class="flex-1 grid grid-cols-3 gap-2 text-center">
                        <div>
                            <div class="text-[10px] text-slate-500 uppercase">Wins</div>
                            <div class="text-sm font-bold text-green-400">${weekWins}</div>
                        </div>
                        <div>
                            <div class="text-[10px] text-slate-500 uppercase">Loss</div>
                            <div class="text-sm font-bold text-red-400">${weekLosses}</div>
                        </div>
                        <div>
                            <div class="text-[10px] text-slate-500 uppercase">WR</div>
                            <div class="text-sm font-bold text-blue-400">${weekWR}%</div>
                        </div>
                    </div>
                </div>
            `;
        }

        mobileRecap.innerHTML = html;
    }

    // ✅ FASE 5: RENDERIZAR VISTA ANUAL OPTIMIZADO
    /**
     * Renderiza 12 tarjetas de meses usando caché precalculado
     * Performance: <20ms (usa annualCache, no itera datos)
     */
    renderAnnualCalendar() {
        const year = this.currentDate.getFullYear();
        const today = new Date();
        const currentRealMonth = today.getMonth();
        const currentRealYear = today.getFullYear();
        const monthNamesShort = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
        const monthNamesFull = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

        // 1. Header
        const currentMonthEl = document.getElementById('currentMonth');
        if (currentMonthEl) currentMonthEl.textContent = year.toString();

        // 2. Contenedor
        const calendarDays = document.getElementById('calendarDays');
        if (!calendarDays || !this.annualCache[year]) return;
        calendarDays.innerHTML = '';

        // 3. Datos auxiliares
        const accountData = window.accounts?.[window.currentAccount];
        const initialBalance = accountData?.initial_balance || accountData?.balance || 10000;
        const fmtUSD = (v) => (v >= 0 ? '+' : '') + '$' + Math.abs(v).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

        // 4. Grid 12 meses
        const wrap = document.createElement('div');
        wrap.className = 'space-y-4';

        const grid = document.createElement('div');
        grid.className = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4';

        for (let month = 0; month < 12; month++) {
            const m = this.annualCache[year].months[month];
            const perf = parseFloat(m.perf);
            const isProfit = perf >= 0;
            const hasData = m.ops > 0;
            const isCurrentMonth = year === currentRealYear && month === currentRealMonth;
            const isFuture = year === currentRealYear && month > currentRealMonth;
            const pf = !hasData ? '--' : m.grossLoss === 0 ? (m.grossProfit > 0 ? m.grossProfit.toFixed(1) : '--') : (m.grossProfit / m.grossLoss).toFixed(2);
            const pct = hasData ? ((perf / initialBalance) * 100) : null;

            let borderClass, bgClass, glowClass = '';
            if (isCurrentMonth) {
                borderClass = isProfit ? 'border-emerald-500/70' : 'border-red-500/70';
                bgClass = 'bg-slate-800/80';
                glowClass = isProfit ? 'shadow-[0_0_12px_rgba(52,211,153,0.12)]' : 'shadow-[0_0_12px_rgba(248,113,113,0.12)]';
            } else if (isFuture || !hasData) {
                borderClass = 'border-transparent';
                bgClass = 'bg-slate-800/20';
            } else if (isProfit) {
                borderClass = 'border-emerald-500/50 hover:border-emerald-400/70';
                bgClass = 'bg-emerald-950/20 hover:bg-emerald-950/30';
            } else {
                borderClass = 'border-red-500/50 hover:border-red-400/70';
                bgClass = 'bg-red-950/20 hover:bg-red-950/30';
            }

            const card = document.createElement('div');
            card.className = `relative rounded-xl border ${bgClass} ${borderClass} ${glowClass} transition-all duration-200 overflow-hidden ${!isFuture && hasData ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg' : ''} ${isFuture ? 'opacity-40' : ''}`;

            card.innerHTML = `
                <div class="p-4">
                    <div class="flex items-center justify-between mb-3">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-bold text-slate-200">${monthNamesFull[month]}</span>
                            ${isCurrentMonth ? `<span class="text-[9px] font-bold bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded-full uppercase tracking-wide">Actual</span>` : ''}
                        </div>
                        ${isCurrentMonth ? `<span class="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>` : ''}
                    </div>

                    ${hasData ? `
                    <div class="mb-3">
                        <div class="text-xl font-black leading-tight ${isProfit ? 'text-emerald-400' : 'text-red-400'}">${fmtUSD(perf)}</div>
                        <div class="text-xs font-medium mt-0.5 ${isProfit ? 'text-emerald-500/80' : 'text-red-500/80'}">${pct !== null ? (isProfit ? '+' : '') + pct.toFixed(1) + '%' : ''}</div>
                    </div>

                    <div class="grid grid-cols-3 gap-1.5 text-center">
                        <div class="bg-slate-900/50 rounded-lg py-1.5">
                            <div class="text-xs font-bold text-white">${m.ops}</div>
                            <div class="text-[9px] text-slate-500 mt-0.5">Ops</div>
                        </div>
                        <div class="bg-slate-900/50 rounded-lg py-1.5">
                            <div class="text-xs font-bold text-violet-400">${pf}</div>
                            <div class="text-[9px] text-slate-500 mt-0.5">PF</div>
                        </div>
                        <div class="bg-slate-900/50 rounded-lg py-1.5">
                            <div class="text-xs font-bold text-cyan-400">${m.wr}%</div>
                            <div class="text-[9px] text-slate-500 mt-0.5">WR</div>
                        </div>
                    </div>
                    <div class="mt-2 text-right">
                        <span class="text-[10px]"><span class="text-emerald-500">${m.wins}W</span><span class="text-slate-600 mx-0.5">/</span><span class="text-red-500">${m.losses}L</span>${m.beCount > 0 ? `<span class="text-slate-600 ml-1">${m.beCount}BE</span>` : ''}</span>
                    </div>
                    ` : `
                    <div class="py-5 text-center">
                        <div class="text-slate-600 text-xs">${isFuture ? 'Sin empezar' : 'Sin operaciones'}</div>
                    </div>
                    `}
                </div>
            `;

            grid.appendChild(card);
        }

        wrap.appendChild(grid);
        calendarDays.appendChild(wrap);
    }

    // ✅ FASE 6: ACTUALIZAR MÉTRICAS ANUALES EN KPI CARDS
    /**
     * Actualiza elementos DOM con métricas anuales del caché
     * Performance: <10ms (sin iterar, acceso directo al caché)
     */
    updateAnnualMetrics() {
        const year = this.currentDate.getFullYear();
        if (!this.annualCache[year]) return;

        // Acumular totales del año
        let annualProfit = 0, annualOps = 0, annualWins = 0, annualLosses = 0, annualBE = 0;
        let annualGrossProfit = 0, annualGrossLoss = 0;
        let bestTradeObj = null, worstTradeObj = null;
        let currentStreak = 0, bestStreak = 0, worstStreak = 0;
        const allTrades = [];

        // Recoger todos los trades del año ordenados por fecha
        const src = this._annualYearData || window.journalData || {};
        const accountData = window.accounts?.[window.currentAccount];
        const initialBalance = accountData?.initial_balance || accountData?.balance || 10000;

        Object.keys(src).sort().forEach(dateStr => {
            if (!dateStr.startsWith(year + '-')) return;
            (src[dateStr] || []).forEach(t => {
                allTrades.push({ ...t, _date: dateStr });
            });
        });

        allTrades.forEach(t => {
            const profit = parseFloat(t.profitUSD || 0);
            const profitPct = (profit / initialBalance) * 100;
            const isBE = profitPct >= -0.1 && profitPct <= 0.1;

            annualProfit += profit;
            annualOps++;

            if (isBE) {
                annualBE++;
            } else if (profit > 0) {
                annualWins++;
                annualGrossProfit += profit;
                if (!bestTradeObj || profit > parseFloat(bestTradeObj.profitUSD || 0)) bestTradeObj = t;
            } else {
                annualLosses++;
                annualGrossLoss += Math.abs(profit);
                if (!worstTradeObj || profit < parseFloat(worstTradeObj.profitUSD || 0)) worstTradeObj = t;
            }

            // Racha
            if (!isBE) {
                if (currentStreak === 0) {
                    currentStreak = profit > 0 ? 1 : -1;
                } else if (profit > 0 && currentStreak > 0) {
                    currentStreak++;
                } else if (profit < 0 && currentStreak < 0) {
                    currentStreak--;
                } else {
                    currentStreak = profit > 0 ? 1 : -1;
                }
                if (currentStreak > bestStreak) bestStreak = currentStreak;
                if (currentStreak < worstStreak) worstStreak = currentStreak;
            }
        });

        const nonBE = annualOps - annualBE;
        const annualWR = nonBE > 0 ? Math.round((annualWins / nonBE) * 100) : 0;
        const annualAvg = annualOps > 0 ? (annualProfit / annualOps) : 0;
        const annualPF = annualGrossLoss === 0
            ? (annualGrossProfit > 0 ? annualGrossProfit.toFixed(2) : '--')
            : (annualGrossProfit / annualGrossLoss).toFixed(2);
        const percentChange = (annualProfit / initialBalance) * 100;

        // --- Actualizar DOM ---

        // Win Rate
        const winRatePercentEl = document.getElementById('winRatePercent');
        if (winRatePercentEl) winRatePercentEl.textContent = `${annualWR}%`;
        const winRateBarEl = document.getElementById('winRateBar');
        if (winRateBarEl) winRateBarEl.style.width = `${annualWR}%`;
        const winRateRatioEl = document.getElementById('winRateRatio');
        if (winRateRatioEl) {
            winRateRatioEl.textContent = annualBE > 0
                ? `${annualWins}/${nonBE} - ${annualBE} BE`
                : `${annualWins}/${nonBE}`;
        }

        // Trades (label + valores)
        const tradesPeriodLabel = document.getElementById('tradesPeriodLabel');
        if (tradesPeriodLabel) tradesPeriodLabel.textContent = 'Trades Año';
        const tradesThisMonthEl = document.getElementById('tradesThisMonth');
        if (tradesThisMonthEl) tradesThisMonthEl.textContent = annualOps;
        const tradesWonEl = document.getElementById('tradesWon');
        if (tradesWonEl) tradesWonEl.textContent = annualWins;
        const tradesLostEl = document.getElementById('tradesLost');
        if (tradesLostEl) tradesLostEl.textContent = annualLosses;
        const tradesBEEl = document.getElementById('tradesBE');
        if (tradesBEEl) tradesBEEl.textContent = annualBE;

        // Resultado
        const resultUSDEl = document.getElementById('monthResultUSD');
        if (resultUSDEl) {
            resultUSDEl.textContent = `${annualProfit >= 0 ? '+' : ''}$${Math.abs(annualProfit).toFixed(2)}`;
            resultUSDEl.className = annualProfit >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold';
        }
        const resultPercentEl = document.getElementById('monthResult');
        if (resultPercentEl) {
            resultPercentEl.textContent = `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(2)}%`;
            resultPercentEl.className = percentChange >= 0 ? 'text-green-400' : 'text-red-400';
        }

        // Profit Factor + Avg Win
        const profitFactorEl = document.getElementById('profitFactor');
        if (profitFactorEl) profitFactorEl.textContent = annualPF;
        const avgWinEl = document.getElementById('avgWin');
        if (avgWinEl) avgWinEl.textContent = `$${Math.abs(annualAvg).toFixed(2)}`;

        // Racha
        const currentStreakEl = document.getElementById('currentStreak');
        if (currentStreakEl) {
            const sign = currentStreak > 0 ? '+' : '';
            currentStreakEl.textContent = currentStreak === 0 ? '0' : `${sign}${currentStreak}`;
            currentStreakEl.className = `text-3xl font-bold mb-3 ${currentStreak > 0 ? 'text-green-400' : currentStreak < 0 ? 'text-red-400' : 'text-slate-400'}`;
        }
        const bestStreakEl = document.getElementById('bestStreak');
        if (bestStreakEl) bestStreakEl.textContent = bestStreak;
        const worstStreakEl = document.getElementById('worstStreak');
        if (worstStreakEl) worstStreakEl.textContent = worstStreak;

        // Destacados: label + mejor/peor trade del año
        const highlightsPeriodLabel = document.getElementById('highlightsPeriodLabel');
        if (highlightsPeriodLabel) highlightsPeriodLabel.textContent = 'Destacados del Año';

        if (bestTradeObj) {
            const bestProfitPct = ((parseFloat(bestTradeObj.profitUSD) / initialBalance) * 100).toFixed(2);
            const el = document.getElementById('bestTradePair');
            if (el) el.textContent = bestTradeObj.pair || bestTradeObj.symbol || '--';
            const dirEl = document.getElementById('bestTradeDirection');
            if (dirEl) {
                const dir = (bestTradeObj.direction || bestTradeObj.type || '').toUpperCase();
                dirEl.textContent = dir || '--';
                dirEl.className = `text-[10px] px-1.5 py-0.5 rounded ${dir === 'BUY' || dir === 'LONG' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`;
            }
            const profEl = document.getElementById('bestTradeProfit');
            if (profEl) profEl.textContent = `+${bestProfitPct}%`;
            const dateEl = document.getElementById('bestTradeDate');
            if (dateEl) dateEl.textContent = bestTradeObj._date || '--';
        }

        if (worstTradeObj) {
            const worstProfitPct = ((parseFloat(worstTradeObj.profitUSD) / initialBalance) * 100).toFixed(2);
            const el = document.getElementById('worstTradePair');
            if (el) el.textContent = worstTradeObj.pair || worstTradeObj.symbol || '--';
            const dirEl = document.getElementById('worstTradeDirection');
            if (dirEl) {
                const dir = (worstTradeObj.direction || worstTradeObj.type || '').toUpperCase();
                dirEl.textContent = dir || '--';
                dirEl.className = `text-[10px] px-1.5 py-0.5 rounded ${dir === 'BUY' || dir === 'LONG' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`;
            }
            const profEl = document.getElementById('worstTradeProfit');
            if (profEl) profEl.textContent = `${worstProfitPct}%`;
            const dateEl = document.getElementById('worstTradeDate');
            if (dateEl) dateEl.textContent = worstTradeObj._date || '--';
        }

        // Avg Trade Duration (anual)
        const durationPeriodLabel = document.getElementById('durationPeriodLabel');
        if (durationPeriodLabel) durationPeriodLabel.textContent = '(anual)';
        if (typeof window.updateTradeDuration === 'function') {
            window.updateTradeDuration(allTrades, initialBalance);
        }

        // Equity Curve (rango anual)
        // Calcular balance al inicio del año sumando todos los trades anteriores
        if (typeof window.updateEquityCurve === 'function') {
            const allGlobal = [];
            const src2 = window.journalData || {};
            Object.keys(src2).sort().forEach(dateStr => {
                (src2[dateStr] || []).forEach(t => allGlobal.push({ ...t, _date: dateStr }));
            });
            // Balance al comienzo del año
            let balanceAtYearStart = initialBalance;
            allGlobal.forEach(t => {
                const d = (t.trade_date || t.date || t._date || '');
                if (d < `${year}-01-01`) {
                    balanceAtYearStart += parseFloat(t.profitUSD || 0);
                }
            });
            // Pasar trades del año con un trade "fantasma" inicial para fijar el balance de partida
            const annualTradesForEquity = allTrades.map(t => ({ ...t }));
            window.updateEquityCurve(annualTradesForEquity, balanceAtYearStart);
        }

        // Shorts vs Longs (anual)
        if (typeof window.updateShortsLongsChart === 'function') {
            window.updateShortsLongsChart(allTrades, initialBalance);
        }
    }

    updateMonthStats() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();

        let totalTrades = 0;

        // Calcular stats del mes
        Object.keys(this.tradesData).forEach(dateStr => {
            const date = new Date(dateStr);
            if (date.getFullYear() === year && date.getMonth() === month) {
                totalTrades += this.tradesData[dateStr].length;
            }
        });

        // Actualizar contador de trades totales del mes
        const activeDaysElement = document.getElementById('activeDaysCount');
        if (activeDaysElement) {
            activeDaysElement.textContent = `${totalTrades} trade${totalTrades !== 1 ? 's' : ''} totales`;
        }
    }

    // Método para limpiar el calendario cuando se cambia de cuenta
    clearCalendar() {
        this.tradesData = {};
        this.currentAccountId = null;
        const calendarDays = document.getElementById('calendarDays');
        if (calendarDays) {
            calendarDays.innerHTML = '';
        }
        const activeDaysElement = document.getElementById('activeDaysCount');
        if (activeDaysElement) {
            activeDaysElement.textContent = '0 trades totales';
        }
    }
}

// Inicializar calendario cuando el DOM esté listo
let journalCalendar;
document.addEventListener('DOMContentLoaded', () => {
    journalCalendar = new JournalCalendar();
    window.journalCalendar = journalCalendar;
});
